import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";

// Import your context and API
import { useGlobalContext } from "../ContextApi/GlobalContext";
import { loginCompany } from "../Backend/authFunction"; // Check path

// Validation Schema
const schema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  password: yup
    .string()
    .min(6, "Min 6 characters")
    .required("Password is required"),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { dispatch } = useGlobalContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoader(true);
    try {
      const result = await loginCompany(data);
      if (result.success) {
        dispatch({
          authToken: result.data.token,
          companyData: result.data,
        });

        setLoader(false);

        Swal.fire({
          icon: "success",
          title: "Welcome back!",
          text: "Login successful",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/dashboard");
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setLoader(false);
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.message || "Invalid credentials",
        confirmButtonColor: "#ef4444",
      });
    }
  };
  return (
    <div>
      <div className="min-h-screen flex bg-[#F6F8FB] font-sans overflow-x-hidden pt-20 lg:pt-0">
        {/* LEFT SIDE - BRANDING (Hidden on mobile) */}
        <div className="hidden lg:flex w-5/12 bg-[#0B1C2D] relative overflow-hidden items-center justify-center p-12">
          {/* Background Effects (Matches Register Page) */}
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#1a2f46] via-[#0B1C2D] to-[#0B1C2D]"></div>
          <div className="absolute top-1/4 -left-12 w-96 h-96 bg-[#E63946]/10 rounded-full blur-[100px]"></div>

          <div className="relative z-10 max-w-lg">
            <a
              href="/"
              className="flex items-center gap-3 group cursor-pointer mb-6"
            >
              <div className="flex items-center justify-center size-10 rounded-lg bg-[#E63946] text-white transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3 shadow-lg shadow-[#E63946]/20">
                <span className="material-symbols-outlined">hub</span>
              </div>
              <span className="text-xl font-bold tracking-tight text-white">
                ContactHQ
              </span>
            </a>

            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              Welcome back to <br />
              your <span className="text-[#E63946]">Control Center.</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed mb-10">
              Log in to manage your forms, view analytics, and respond to
              customer queries in real-time.
            </p>

            {/* Testimonial */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-2xl">
              <div className="flex text-yellow-400 mb-3 gap-1">
                {[...Array(5)].map((_, i) => (
                  <span key={i}>★</span>
                ))}
              </div>
              <p className="text-gray-300 italic mb-4">
                "Managing multiple client forms across Delhi was chaotic. Now,
                we track responses and collaborate without ever touching a
                spreadsheet. Highly recommended for agencies."
              </p>
              <div className="flex items-center gap-3">
                <img
                  src="https://i.pravatar.cc/100?u=Sayani Verma"
                  alt="User"
                  className="w-10 h-10 rounded-full border-2 border-[#E63946]"
                />
                <div>
                  <p className="text-white text-sm font-bold">Sayani Verma</p>
                  <p className="text-[#E63946] text-xs font-semibold">
                    Digital Marketing Head, Delhi
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - LOGIN FORM */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md bg-white p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 relative animate-fade-in-up">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#E63946]/10 text-[#E63946] mb-4">
                <span className="material-symbols-outlined text-2xl">lock</span>
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-[#0B1C2D]">
                Welcome back
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                Enter your details to access your dashboard.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium leading-6 text-[#0B1C2D] mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    {...register("email")}
                    className={`block w-full rounded-lg border-0 py-3 pl-4 text-[#0B1C2D] shadow-sm ring-1 ring-inset ${
                      errors.email
                        ? "ring-red-300 focus:ring-red-500"
                        : "ring-gray-200 focus:ring-[#E63946]"
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-all outline-none`}
                    placeholder="name@company.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium leading-6 text-[#0B1C2D]">
                    Password
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-xs font-semibold text-[#E63946] hover:text-[#d12f3c] hover:underline"
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register("password")}
                    className={`block w-full rounded-lg border-0 py-3 pl-4 pr-10 text-[#0B1C2D] shadow-sm ring-1 ring-inset ${
                      errors.password
                        ? "ring-red-300 focus:ring-red-500"
                        : "ring-gray-200 focus:ring-[#E63946]"
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-all outline-none`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#0B1C2D]"
                  >
                    <span className="material-symbols-outlined text-[20px]">
                      {showPassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loader}
                className="w-full flex justify-center items-center gap-2 rounded-lg bg-[#E63946] px-8 py-3 text-base font-semibold text-white shadow-md shadow-[#E63946]/20 hover:bg-[#d12f3c] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loader ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="font-semibold text-[#E63946] hover:text-[#d12f3c] transition-colors"
                >
                  Register
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
